import { useState, type JSX } from 'react';
import { DialMark } from '@procuai/ui';
import { requestLink, ApiError } from '../api.js';

/**
 * Sign-in screen (BP-01 §5). Magic-link-first: enter an email, receive a one-time link. Branded to
 * D-064 (dial + wordmark, §14 tokens). The password / TOTP / org-choice states from D-080 are added
 * when those flows are needed; magic link is the only path today.
 */

/**
 * Best-effort "open your inbox" target. A web page cannot open the OS default mail app's inbox, so we
 * map the email's domain to its webmail inbox. Known consumer providers resolve exactly; unknown
 * (corporate) domains default to Microsoft 365 Outlook on the web, the most common business host.
 */
function webmail(email: string): { url: string; label: string } {
  const domain = (email.split('@')[1] ?? '').toLowerCase().trim();
  const ms = { url: 'https://outlook.live.com/mail/0/', label: 'Open Outlook' };
  const gm = { url: 'https://mail.google.com/mail/u/0/', label: 'Open Gmail' };
  const map: Record<string, { url: string; label: string }> = {
    'gmail.com': gm,
    'googlemail.com': gm,
    'outlook.com': ms,
    'hotmail.com': ms,
    'live.com': ms,
    'msn.com': ms,
    'yahoo.com': { url: 'https://mail.yahoo.com/', label: 'Open Yahoo Mail' },
    'icloud.com': { url: 'https://www.icloud.com/mail/', label: 'Open iCloud Mail' },
    'me.com': { url: 'https://www.icloud.com/mail/', label: 'Open iCloud Mail' },
    'proton.me': { url: 'https://mail.proton.me/u/0/', label: 'Open Proton Mail' },
    'protonmail.com': { url: 'https://mail.proton.me/u/0/', label: 'Open Proton Mail' },
  };
  return map[domain] ?? { url: 'https://outlook.office.com/mail/', label: 'Open your email' };
}

export function Login(): JSX.Element {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [isError, setIsError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setIsError(false);
    try {
      await requestLink(email.trim().toLowerCase());
      setSent(true);
      setMsg('Check your inbox for a secure sign-in link. It works once and expires in 15 minutes.');
    } catch (err) {
      setIsError(true);
      setMsg(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const inbox = webmail(email);

  return (
    <div className="auth">
      <div className="auth-card">
        <div className="auth-brand">
          <DialMark size={26} />
          <span className="auth-wordmark">procu<span>ai</span></span>
        </div>
        <h1>Sign in</h1>
        <p className="auth-sub">
          Enter your work email and we will send you a secure sign-in link. No password to remember.
        </p>
        <form onSubmit={submit}>
          <input
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
          />
          <button className="btn btn-primary auth-btn" disabled={busy || sent}>
            {busy ? 'Sending...' : sent ? 'Link sent' : 'Email me a sign-in link'}
          </button>
        </form>
        {msg && <div className={isError ? 'auth-msg error' : 'auth-msg'}>{msg}</div>}
        {sent && (
          <a className="btn btn-outline auth-btn auth-inbox" href={inbox.url} target="_blank" rel="noopener noreferrer">
            {inbox.label}
          </a>
        )}
      </div>
      <div className="auth-trust">
        <DialMark size={12} /> Encrypted and hosted in the EU. ProcuAI, the system of control.
      </div>
    </div>
  );
}
